import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import {
  assignSuccessor,
  employeeLabelOf,
  getSuccessors,
  listEmployees,
  SUCCESSOR_REASONS,
  type EmployeeRow,
  type SuccessorReason,
} from '../../api/orgApi.js';
import { CheckboxField, Field, SelectField, StatusBadge, sx, toOptions, todayIso } from './orgKit.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SuccessorRow {
  successorId?: string;
  successorEmployeeId?: string;
  reason?: string;
  effectiveDate?: string;
  endDate?: string | null;
  isPermanent?: boolean;
  approvalStatus?: string;
  recordStatusCode?: string;
}

export function SuccessorMappingPage(): ReactNode {
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [rows, setRows] = useState<SuccessorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Add-successor form fields
  const [successorEmployeeId, setSuccessorEmployeeId] = useState('');
  const [reason, setReason] = useState<SuccessorReason>('RESIGNATION');
  const [effectiveDate, setEffectiveDate] = useState(todayIso());
  const [endDate, setEndDate] = useState('');
  const [isPermanent, setIsPermanent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  const empName = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of employees) m.set(e.empExtId, employeeLabelOf(e));
    return (id?: string): string => (id ? m.get(id) ?? id : '—');
  }, [employees]);

  function loadSuccessors(id: string): void {
    if (!id) { setRows([]); return; }
    setLoading(true);
    setError(null);
    getSuccessors(token, id)
      .then((r) => setRows(r.data as SuccessorRow[]))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  function onPickEmployee(id: string): void {
    setEmployeeId(id);
    setNotice(null);
    loadSuccessors(id);
  }

  async function submit(): Promise<void> {
    if (!employeeId) { setError(t('org.successor.selectFirst')); return; }
    if (!successorEmployeeId) { setError(t('org.successor.selectSuccessor')); return; }
    if (successorEmployeeId === employeeId) { setError(t('org.successor.differentEmp')); return; }
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await assignSuccessor(token, employeeId, {
        successorEmployeeId,
        reason,
        effectiveDate,
        endDate: endDate || undefined,
        isPermanent,
      });
      setNotice(t('org.successor.added'));
      setSuccessorEmployeeId('');
      setEndDate('');
      setIsPermanent(false);
      loadSuccessors(employeeId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const employeeOptions = employees.map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));
  const successorOptions = employees
    .filter((e) => e.empExtId !== employeeId)
    .map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));

  return (
    <main aria-label="successor-mapping" style={sx.main}>
      <h1>{t('org.successor.title')}</h1>
      {error ? <p role="alert" style={sx.error}>{error}</p> : null}
      {notice ? <p style={sx.ok}>{notice}</p> : null}

      <SelectField
        label={t('org.field.employee')}
        name="employeeId"
        value={employeeId}
        onChange={onPickEmployee}
        options={employeeOptions}
        placeholder={t('org.successor.employeePh')}
      />

      {employeeId ? (
        <>
          <section aria-label="successor-list" style={{ marginTop: 20 }}>
            <h2>{t('org.successor.current')}</h2>
            {loading ? <p>{t('common.loading')}</p> : null}
            {!loading && rows.length === 0 ? <p>{t('org.successor.empty')}</p> : null}
            {rows.length > 0 && (
              <DataTable<SuccessorRow>
                rowKey={(s) => s.successorId ?? s.successorEmployeeId ?? ''}
                rows={rows}
                columns={[
                  { key: 'successor', header: t('org.col.successor'), render: (s) => empName(s.successorEmployeeId) },
                  { key: 'reason', header: t('org.col.reason'), render: (s) => s.reason ?? '—' },
                  { key: 'effective', header: t('org.col.effective'), render: (s) => day(s.effectiveDate) },
                  { key: 'end', header: t('org.col.end'), render: (s) => day(s.endDate) },
                  { key: 'permanent', header: t('org.col.permanent'), render: (s) => s.isPermanent ? t('org.common.yes') : t('org.common.no') },
                  { key: 'approval', header: t('org.col.approval'), render: (s) => <StatusBadge status={s.approvalStatus ?? '—'} /> },
                ]}
              />
            )}
          </section>

          <section aria-label="add-successor" style={{ ...sx.card, marginTop: 20 }}>
            <h2>{t('org.successor.addTitle')}</h2>
            <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <SelectField label={t('org.successor.successorEmployee')} name="successorEmployeeId" value={successorEmployeeId} onChange={setSuccessorEmployeeId} options={successorOptions} required />
              <SelectField label={t('org.field.reason')} name="reason" value={reason} onChange={(v) => setReason(v as SuccessorReason)} options={toOptions(SUCCESSOR_REASONS)} required />
              <Field label={t('org.field.effectiveDate')} name="effectiveDate" value={effectiveDate} onChange={setEffectiveDate} type="date" required />
              <Field label={t('org.field.endDate')} name="endDate" value={endDate} onChange={setEndDate} type="date" />
              <CheckboxField label={t('org.successor.permanent')} name="isPermanent" checked={isPermanent} onChange={setIsPermanent} />
              <div style={sx.actionBar}>
                <Button type="submit" variant="primary" disabled={busy} aria-label="save">
                  {busy ? t('org.kit.saving') : t('org.successor.addBtn')}
                </Button>
              </div>
            </form>
          </section>
        </>
      ) : null}
    </main>
  );
}
